import { useMsal } from '@azure/msal-react';
import { AccountInfo } from '@azure/msal-browser';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/api/client';

interface UserInfo {
  id: string;
  email: string;
  name: string;
  permissions: string[];
  roles: Array<{
    id: string;
    name: string;
    description?: string;
  }>;
}

export const useAuth = () => {
  const { instance, accounts } = useMsal();

  const account = accounts[0] as AccountInfo | undefined;

  const isAuthenticated = !!account;

  // Fetch user permissions from backend
  const { data: userInfo } = useQuery<UserInfo>({
    queryKey: ['userInfo', account?.homeAccountId],
    queryFn: async () => {
      return apiClient.get<UserInfo>('/api/mobile/user/info');
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const user = account
    ? {
        id: account.homeAccountId,
        email: account.username,
        name: account.name || account.username,
        permissions: userInfo?.permissions || [],
        roles: userInfo?.roles || [],
      }
    : null;

  const login = async () => {
    await instance.loginRedirect({
      scopes: ['User.Read', 'email', 'profile'],
      prompt: 'select_account',
    });
  };

  const logout = async () => {
    try {
      // Clear MSAL interaction status first to prevent conflicts
      const interactionStatusKeys = Object.keys(localStorage).filter(key =>
        key.includes('interaction.status')
      );
      interactionStatusKeys.forEach(key => localStorage.removeItem(key));

      // Check if there's an active account before attempting logout
      if (account) {
        await instance.logoutRedirect({
          account,
        });
      } else {
        // If no account, just clear relevant storage and reload
        // Preserve only critical MSAL keys to avoid orphaned state
        const keysToRemove = Object.keys(localStorage).filter(key =>
          !key.includes('msal.token') && !key.includes('msal.account')
        );
        keysToRemove.forEach(key => localStorage.removeItem(key));
        window.location.href = '/';
      }
    } catch (error: any) {
      // Handle interaction_in_progress error
      if (error?.errorCode === 'interaction_in_progress') {
        console.warn('Interaction already in progress, forcing logout...');
        // Clear all MSAL data to force clean state
        Object.keys(localStorage).forEach(key => {
          if (key.includes('msal')) {
            localStorage.removeItem(key);
          }
        });
        window.location.href = '/';
      } else {
        console.error('Logout error:', error);
        // Clear all MSAL data on other errors too
        Object.keys(localStorage).forEach(key => {
          if (key.includes('msal')) {
            localStorage.removeItem(key);
          }
        });
        window.location.href = '/';
      }
    }
  };

  const isValidDomain = () => {
    if (!account) return false;
    const email = account.username?.toLowerCase();
    return email?.endsWith('@horwathhtl.com') || email?.endsWith('@barretthtl.com');
  };

  return {
    isAuthenticated: isAuthenticated && isValidDomain(),
    user,
    login,
    logout,
  };
};
